// illustrations.jsx - Drawn artwork for the customer site.
//
// Line art rather than photography, on purpose. Stock photographs of jewellery
// are the single most recognisable tell of a template site, and real photographs
// of our own bench are something to shoot later and drop into lib/media.js.
// Until then this gives every page something made rather than something bought.
//
// House style: one weight of stroke at 1.4, warm ink, bronze reserved for the
// thing the eye should land on, and a little hatching for depth. Everything
// scales cleanly because nothing is bitmap.
//
// LEGEND
//   1. Art            the shared frame: viewBox, defs, unique gradient ids
//   2. BalanceScene   the hero, a jeweller's balance with a chain and rings
//   3. MailerKit      the padded envelope with its prepaid label
//   4. item art       ChainCoil, RingSolitaire, EarringPair, Flatware,
//                     CoinStack, BarStack, PocketWatch, DentalCrown
//   5. process art    Loupe, VideoRecord, TruckRoute, PayoutEnvelope
//   6. reference art  HallmarkZoom, UsMap
//   7. decoration     Ornament, GrainDefs

import { useId } from "react";
import clsx from "clsx";

////////// 1. FRAME //////////
// Gradient ids have to be unique per instance or a second copy of the same
// illustration on one page steals the first one's fill.

function Art({ viewBox, className, label, children, style }) {
  const raw = useId();
  const uid = raw.replace(/[^a-zA-Z0-9]/g, "");

  return (
    <svg
      viewBox={viewBox}
      className={clsx("block w-full h-auto", className)}
      style={style}
      role={label ? "img" : "presentation"}
      aria-label={label}
      aria-hidden={label ? undefined : "true"}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient id={`warm-${uid}`} cx="50%" cy="42%" r="62%">
          <stop offset="0%" stopColor="var(--color-bronze-soft)" stopOpacity="0.28" />
          <stop offset="60%" stopColor="var(--color-bronze-soft)" stopOpacity="0.07" />
          <stop offset="100%" stopColor="var(--color-bronze-soft)" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`metal-${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--color-bronze-soft)" stopOpacity="0.55" />
          <stop offset="52%" stopColor="var(--color-bronze)" stopOpacity="0.32" />
          <stop offset="100%" stopColor="var(--color-bronze-soft)" stopOpacity="0.5" />
        </linearGradient>
        <pattern id={`hatch-${uid}`} width="5" height="5" patternTransform="rotate(38)" patternUnits="userSpaceOnUse">
          <line x1="0" y1="0" x2="0" y2="5" stroke="var(--color-bronze)" strokeWidth="0.7" strokeOpacity="0.34" />
        </pattern>
      </defs>
      {children({ warm: `url(#warm-${uid})`, metal: `url(#metal-${uid})`, hatch: `url(#hatch-${uid})` })}
    </svg>
  );
}

// Shared stroke settings. Passed down rather than repeated on forty elements.
const ink = {
  stroke: "var(--color-ink)",
  strokeWidth: 1.4,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};
const thin = { ...ink, strokeWidth: 1, strokeOpacity: 0.55 };
const bronze = { ...ink, stroke: "var(--color-bronze)" };

////////// 2. HERO //////////
// A balance is the one object that means exactly what this business does: your
// things on one side, a number on the other. The beam carries a class so the CSS
// can rock it a degree and a half, which is the only movement in the drawing.

export function BalanceScene({ className, label = "A jeweller's balance weighing a gold chain against a set of rings" }) {
  return (
    <Art viewBox="0 0 540 460" className={className} label={label}>
      {({ warm, metal, hatch }) => (
        <>
          <circle cx="270" cy="196" r="186" fill={warm} />

          {/* No floor line. On the home page a card overlaps the lower left of
              this drawing, and any horizontal rule pokes out beside it looking
              like a stray mark. The base ellipse grounds it on its own. */}

          {/* Pedestal */}
          <ellipse cx="270" cy="410" rx="74" ry="14" fill="var(--color-surface)" {...ink} />
          <path d="M240 410c5-34 12-58 15-88h30c3 30 10 54 15 88Z" fill="var(--color-surface)" {...ink} />
          <path d="M246 386h48" {...thin} />
          <rect x="261" y="150" width="18" height="174" rx="3" fill="var(--color-surface)" {...ink} />
          <rect x="261" y="150" width="7" height="174" fill={hatch} stroke="none" />
          <circle cx="270" cy="140" r="10" fill={metal} {...bronze} />

          {/* Beam and pans. The group rocks; the pans hang from it. */}
          <g className="balance-beam" style={{ transformOrigin: "270px 156px" }}>
            <line x1="96" y1="176" x2="444" y2="138" {...ink} strokeWidth="3" />
            <circle cx="270" cy="157" r="6" fill="var(--color-ink)" />
            <path d="M262 150h16" {...thin} />

            {/* Left pan, loaded, so it hangs low */}
            <path d="M96 176 62 254M96 176l32 78" {...thin} />
            <path d="M56 254h78" {...ink} />
            <path d="M52 254c12 30 74 30 86 0" fill="var(--color-surface)" {...ink} />
            {/* Coiled chain in the pan */}
            <g {...bronze} strokeWidth="1.6">
              <path d="M66 250c8-14 26-18 38-9 11 8 6 20-6 21-12 2-22-6-18-16 5-11 24-14 34-6" fill="none" />
              <ellipse cx="72" cy="243" rx="5" ry="3.4" />
              <ellipse cx="84" cy="238" rx="5" ry="3.4" />
              <ellipse cx="97" cy="240" rx="5" ry="3.4" />
              <ellipse cx="107" cy="247" rx="5" ry="3.4" />
              <ellipse cx="95" cy="251" rx="5" ry="3.4" />
              <ellipse cx="82" cy="250" rx="5" ry="3.4" />
            </g>

            {/* Right pan, lighter */}
            <path d="M444 138 414 206M444 138l30 68" {...thin} />
            <path d="M408 206h72" {...ink} />
            <path d="M404 206c11 26 68 26 80 0" fill="var(--color-surface)" {...ink} />
            {/* Two rings, one flat, one standing */}
            <ellipse cx="432" cy="203" rx="18" ry="6" fill="none" {...bronze} />
            <ellipse cx="432" cy="203" rx="10" ry="3.2" fill="none" {...thin} />
            <circle cx="458" cy="192" r="13" fill="none" {...bronze} />
            <circle cx="458" cy="192" r="7.4" fill="none" {...thin} />
            <path d="M452 180l6-7 6 7" fill="var(--color-bronze-soft)" {...bronze} />
          </g>

        </>
      )}
    </Art>
  );
}

////////// 3. KIT //////////

export function MailerKit({ className, label = "A padded mailer with a prepaid insured return label" }) {
  return (
    <Art viewBox="0 0 420 300" className={className} label={label}>
      {({ warm, hatch }) => (
        <>
          <circle cx="210" cy="140" r="140" fill={warm} />

          {/* Envelope body, very slightly rotated for life */}
          <g transform="rotate(-3 210 150)">
            <rect x="34" y="52" width="352" height="204" rx="5" fill="var(--color-surface)" {...ink} />
            <path d="M34 52 210 168 386 52" {...thin} />
            {/* Padding quilt */}
            <g {...thin} strokeOpacity="0.22">
              <path d="M34 92h352M34 132h352M34 172h352M34 212h352" />
              <path d="M94 52v204M154 52v204M266 52v204M326 52v204" />
            </g>

            {/* Label */}
            <rect x="228" y="80" width="130" height="86" rx="3" fill="var(--color-card)" {...bronze} />
            <g {...bronze} strokeOpacity="0.6">
              <path d="M240 98h106M240 110h74M240 122h92" />
            </g>
            {/* Barcode */}
            <g fill="var(--color-ink)" opacity="0.85">
              {Array.from({ length: 26 }, (_, index) => (
                <rect key={index} x={240 + index * 4.3} y={134} width={index % 4 === 0 ? 2.2 : 1.1} height="20" />
              ))}
            </g>
            <rect x="228" y="80" width="130" height="86" rx="3" fill={hatch} stroke="none" opacity="0.25" />

            {/* Return address block */}
            <g {...thin}>
              <path d="M62 190h122M62 206h86M62 222h104" />
            </g>
            <text x="62" y="106" fontFamily="Georgia, serif" fontSize="17" fill="var(--color-ink)">
              International
            </text>
            <text x="62" y="128" fontFamily="Georgia, serif" fontSize="17" fill="var(--color-bronze)">
              Gold &amp; Silver
            </text>
            <text x="62" y="152" fontSize="8.5" letterSpacing="1.8" fill="var(--color-faint)">
              PREPAID · INSURED · FEDEX 2DAY
            </text>
          </g>
        </>
      )}
    </Art>
  );
}

////////// 4. THE THINGS PEOPLE SEND //////////
// Small square drawings, one per category, sized to sit in a grid at 96 to 140px.

const Item = ({ className, label, children }) => (
  <Art viewBox="0 0 120 120" className={className} label={label}>
    {children}
  </Art>
);

// Fourteen links on a circle, each one turned to the tangent, every other one
// edge on. Two rules make it read as a chain rather than as beads: a link is
// always the same length along the chain and only changes width, and each link
// is filled so it covers the one behind it. They are spaced to overlap by about
// four units, which is the interlock.
export const ChainCoil = ({ className }) => (
  <Item className={className} label="A gold chain">
    {({ warm }) => (
      <>
        <circle cx="60" cy="60" r="52" fill={warm} />
        <g {...bronze} strokeWidth="1.7" fill="var(--color-paper)">
          <ellipse cx="91" cy="61" rx="9" ry="5.6" transform="rotate(90 91 61)" />
          <ellipse cx="87.9" cy="74.5" rx="9" ry="2.2" transform="rotate(115.7 87.9 74.5)" />
          <ellipse cx="79.3" cy="85.2" rx="9" ry="5.6" transform="rotate(141.4 79.3 85.2)" />
          <ellipse cx="66.9" cy="91.4" rx="9" ry="2.2" transform="rotate(167.1 66.9 91.4)" />
          <ellipse cx="53.1" cy="91.4" rx="9" ry="5.6" transform="rotate(192.9 53.1 91.4)" />
          <ellipse cx="40.7" cy="85.2" rx="9" ry="2.2" transform="rotate(218.6 40.7 85.2)" />
          <ellipse cx="32.1" cy="74.5" rx="9" ry="5.6" transform="rotate(244.3 32.1 74.5)" />
          <ellipse cx="29" cy="61" rx="9" ry="2.2" transform="rotate(270 29 61)" />
          <ellipse cx="32.1" cy="47.5" rx="9" ry="5.6" transform="rotate(295.7 32.1 47.5)" />
          <ellipse cx="40.7" cy="36.8" rx="9" ry="2.2" transform="rotate(321.4 40.7 36.8)" />
          <ellipse cx="53.1" cy="30.6" rx="9" ry="5.6" transform="rotate(347.1 53.1 30.6)" />
          <ellipse cx="66.9" cy="30.6" rx="9" ry="2.2" transform="rotate(12.9 66.9 30.6)" />
          <ellipse cx="79.3" cy="36.8" rx="9" ry="5.6" transform="rotate(38.6 79.3 36.8)" />
          <ellipse cx="87.9" cy="47.5" rx="9" ry="2.2" transform="rotate(64.3 87.9 47.5)" />
        </g>
      </>
    )}
  </Item>
);

export const RingSolitaire = ({ className }) => (
  <Item className={className} label="A solitaire ring">
    {({ warm, metal }) => (
      <>
        <circle cx="60" cy="60" r="52" fill={warm} />
        <circle cx="60" cy="72" r="26" fill="none" {...bronze} strokeWidth="2.4" />
        <circle cx="60" cy="72" r="18.5" fill="none" {...thin} />
        <path d="M48 42l12-16 12 16-12 12Z" fill={metal} {...ink} />
        <path d="M48 42h24M60 26v28" {...thin} />
        <path d="M52 50l8 4 8-4" {...thin} />
      </>
    )}
  </Item>
);

// Two drop earrings, the second one faded, because we buy the one that is left
// when the other has gone. The old version said that with a dashed circle and a
// small cross, which read as a diagram rather than as a pair of earrings.
const Earring = ({ x, opacity = 1 }) => (
  <g transform={`translate(${x} 0)`} opacity={opacity} fill="none">
    {/* A French wire and a drop. The hook is what makes it an earring rather
        than a bead, so it is drawn at full length and not implied. */}
    <path d="M14 44V33a6 6 0 0 0-12 0" />
    <path d="M14 44c7 6 10 11 10 16a10 10 0 0 1-20 0c0-5 3-10 10-16Z" fill="var(--color-bronze-soft)" fillOpacity="0.3" />
  </g>
);

export const EarringPair = ({ className }) => (
  <Item className={className} label="A single earring and its lost pair">
    {({ warm }) => (
      <>
        <circle cx="60" cy="60" r="52" fill={warm} />
        <g {...bronze} strokeWidth="1.7">
          <Earring x={34} />
          <Earring x={68} opacity={0.3} />
        </g>
      </>
    )}
  </Item>
);

export const Flatware = ({ className }) => (
  <Item className={className} label="Sterling flatware">
    {({ warm, hatch }) => (
      <>
        <circle cx="60" cy="60" r="52" fill={warm} />
        {/* Fork. Four tines, and a handle with width: the old one was a line,
            which read as wire rather than as something you eat with. */}
        <g transform="rotate(-13 40 60)">
          <path d="M31 25v13M36.3 25v13M41.7 25v13M47 25v13" {...ink} />
          <path
            d="M29 37h20c0 7.5-4.2 10.6-7.4 12v41.5c0 3.4-1.2 5.5-2.6 5.5s-2.6-2.1-2.6-5.5V49c-3.2-1.4-7.4-4.5-7.4-12Z"
            fill="var(--color-surface)"
            {...ink}
          />
        </g>
        {/* Spoon */}
        <g transform="rotate(12 78 60)">
          <path d="M78 22c6.4 0 11.5 6.7 11.5 15S84.4 52 78 52s-11.5-6.7-11.5-15S71.6 22 78 22Z" fill="var(--color-surface)" {...ink} />
          <path d="M78 22c6.4 0 11.5 6.7 11.5 15S84.4 52 78 52s-11.5-6.7-11.5-15S71.6 22 78 22Z" fill={hatch} stroke="none" />
          <path
            d="M78 52c-2.6 1.4-2.6 4-2.6 6.2v32.3c0 3.4 1.2 5.5 2.6 5.5s2.6-2.1 2.6-5.5V58.2c0-2.2 0-4.8-2.6-6.2Z"
            fill="var(--color-surface)"
            {...ink}
          />
        </g>
      </>
    )}
  </Item>
);

export const CoinStack = ({ className }) => (
  <Item className={className} label="A stack of silver coins">
    {({ warm, metal }) => (
      <>
        <circle cx="60" cy="60" r="52" fill={warm} />
        {[0, 1, 2, 3].map((index) => (
          <g key={index} transform={`translate(0 ${-index * 11})`}>
            <ellipse cx="52" cy="86" rx="30" ry="10" fill={index === 3 ? metal : "var(--color-surface)"} {...ink} />
          </g>
        ))}
        <ellipse cx="52" cy="53" rx="18" ry="5.6" fill="none" {...thin} />
        <circle cx="92" cy="76" r="16" fill="var(--color-surface)" {...ink} />
        <circle cx="92" cy="76" r="10" fill="none" {...thin} />
      </>
    )}
  </Item>
);

export const BarStack = ({ className }) => (
  <Item className={className} label="Poured metal bars">
    {({ warm, metal, hatch }) => (
      <>
        <circle cx="60" cy="60" r="52" fill={warm} />
        <path d="M22 84h60l10-16H32Z" fill={metal} {...ink} />
        <path d="M22 84v10h60v-10" {...ink} />
        <path d="M38 68h60l10-16H48Z" fill="var(--color-surface)" {...ink} />
        <path d="M38 68v10h60v-10" {...ink} />
        <path d="M38 68h60l10-16H48Z" fill={hatch} stroke="none" />
        <path d="M56 60h30" {...thin} />
      </>
    )}
  </Item>
);

export const PocketWatch = ({ className }) => (
  <Item className={className} label="A gold pocket watch case">
    {({ warm }) => (
      <>
        <circle cx="60" cy="60" r="52" fill={warm} />
        <circle cx="60" cy="68" r="30" fill="var(--color-surface)" {...bronze} strokeWidth="2" />
        <circle cx="60" cy="68" r="23" fill="none" {...thin} />
        <path d="M60 68V52M60 68l12 8" {...ink} />
        <circle cx="60" cy="68" r="2.4" fill="var(--color-ink)" />
        <path d="M54 38v-6h12v6" {...ink} />
        <circle cx="60" cy="27" r="6" fill="none" {...bronze} />
        <g {...thin}>
          <path d="M60 47v4M81 68h-4M60 89v-4M39 68h4" />
        </g>
      </>
    )}
  </Item>
);

export const DentalCrown = ({ className }) => (
  <Item className={className} label="A gold dental crown">
    {({ warm, metal }) => (
      <>
        <circle cx="60" cy="60" r="52" fill={warm} />
        <path d="M40 52c0-12 9-20 20-20s20 8 20 20c0 10-4 14-5 24-1 8-3 14-6 14s-4-8-5-14h-8c-1 6-2 14-5 14s-5-6-6-14c-1-10-5-14-5-24Z" fill={metal} {...ink} />
        <path d="M46 56c4-6 24-6 28 0" {...thin} />
        <path d="M60 32v14" {...thin} />
      </>
    )}
  </Item>
);

////////// 5. PROCESS //////////

export function Loupe({ className }) {
  return (
    <Art viewBox="0 0 160 160" className={className} label="A jeweller's loupe over a hallmark">
      {({ warm, hatch }) => (
        <>
          <circle cx="80" cy="76" r="66" fill={warm} />
          <circle cx="72" cy="68" r="40" fill="var(--color-card)" {...ink} strokeWidth="2" />
          <circle cx="72" cy="68" r="33" fill="none" {...thin} />
          <circle cx="72" cy="68" r="40" fill={hatch} stroke="none" opacity="0.35" />
          <path d="M101 97l30 30" {...ink} strokeWidth="5" />
          <path d="M126 122l14 14" {...ink} strokeWidth="8" strokeLinecap="round" />
          <text x="72" y="76" textAnchor="middle" fontFamily="Georgia, serif" fontSize="22" fill="var(--color-bronze)">585</text>
        </>
      )}
    </Art>
  );
}

export function VideoRecord({ className }) {
  return (
    <Art viewBox="0 0 200 150" className={className} label="A package being recorded on camera">
      {({ warm }) => (
        <>
          <circle cx="100" cy="72" r="72" fill={warm} />
          <rect x="24" y="34" width="118" height="80" rx="4" fill="var(--color-surface)" {...ink} />
          <path d="M142 58l34-18v66l-34-18Z" fill="var(--color-surface)" {...ink} />
          <circle cx="52" cy="74" r="4" fill="var(--color-critical)" />
          <path d="M64 68h44M64 80h30" {...thin} />
          <g {...bronze}>
            <path d="M24 26h118M32 20v6M134 20v6" strokeOpacity="0.5" />
          </g>
          <circle cx="52" cy="74" r="10" fill="none" {...thin} className="pulse-ring" />
        </>
      )}
    </Art>
  );
}

export function TruckRoute({ className }) {
  return (
    <Art viewBox="0 0 220 130" className={className} label="An insured package in transit">
      {({ warm }) => (
        <>
          <circle cx="110" cy="62" r="64" fill={warm} />
          <path d="M12 104h196" {...thin} strokeDasharray="7 7" />
          <rect x="42" y="52" width="72" height="44" rx="3" fill="var(--color-surface)" {...ink} />
          <path d="M114 66h26l20 18v12h-46Z" fill="var(--color-surface)" {...ink} />
          <path d="M120 70h16l13 12h-29Z" fill="none" {...thin} />
          <circle cx="70" cy="100" r="9" fill="var(--color-card)" {...ink} />
          <circle cx="142" cy="100" r="9" fill="var(--color-card)" {...ink} />
          <circle cx="70" cy="100" r="3" fill="var(--color-ink)" />
          <circle cx="142" cy="100" r="3" fill="var(--color-ink)" />
          <g {...bronze} strokeOpacity="0.55">
            <path d="M14 62h20M6 76h28M18 90h16" />
          </g>
          <path d="M60 62h38M60 74h24" {...thin} />
        </>
      )}
    </Art>
  );
}

export function PayoutEnvelope({ className }) {
  return (
    <Art viewBox="0 0 200 150" className={className} label="A payment on its way">
      {({ warm, metal }) => (
        <>
          <circle cx="100" cy="72" r="70" fill={warm} />
          <rect x="34" y="58" width="132" height="72" rx="4" fill="var(--color-surface)" {...ink} />
          <path d="M34 62l66 44 66-44" {...thin} />
          <rect x="66" y="20" width="68" height="46" rx="3" fill="var(--color-card)" {...bronze} />
          <path d="M76 34h48M76 44h30" {...thin} />
          <circle cx="120" cy="46" r="9" fill={metal} {...bronze} />
          <text x="120" y="50" textAnchor="middle" fontFamily="Georgia, serif" fontSize="11" fill="var(--color-bronze-dark)">$</text>
        </>
      )}
    </Art>
  );
}

////////// 6. REFERENCE //////////

export function HallmarkZoom({ className }) {
  return (
    <Art viewBox="0 0 260 170" className={className} label="Where a hallmark is stamped inside a ring">
      {({ warm, hatch }) => (
        <>
          <circle cx="84" cy="86" r="76" fill={warm} />
          <ellipse cx="84" cy="92" rx="52" ry="52" fill="none" {...bronze} strokeWidth="3" />
          <ellipse cx="84" cy="92" rx="38" ry="38" fill="none" {...thin} />
          <path d="M62 122a44 44 0 0 0 44 0" {...ink} strokeWidth="2.4" />
          <path d="M120 108l38 22" {...thin} strokeDasharray="4 4" />
          <path d="M118 78l40-18" {...thin} strokeDasharray="4 4" />
          <rect x="156" y="52" width="96" height="82" rx="4" fill="var(--color-card)" {...ink} />
          <rect x="156" y="52" width="96" height="82" rx="4" fill={hatch} stroke="none" opacity="0.3" />
          <text x="204" y="88" textAnchor="middle" fontFamily="Georgia, serif" fontSize="24" fill="var(--color-ink)">14K</text>
          <text x="204" y="112" textAnchor="middle" fontFamily="Georgia, serif" fontSize="17" fill="var(--color-bronze)">585</text>
        </>
      )}
    </Art>
  );
}

// A deliberately loose outline. It reads as "the whole country" without pretending
// to be a map anybody should navigate by.
// Real geography, not a drawing. The outline is the us-atlas nation topology
// projected with the standard USA Albers parallels, simplified to the point where
// another vertex would not change a pixel at the size this is shown. The dots are
// the coordinates of actual cities, and the bronze one is us, in Prescott.
//
// It replaced a hand drawn blob that was not the shape of any country. A map is
// one of the few things on this page that a person can check at a glance, so it
// has to be right.
export function UsMap({ className }) {
  return (
    <Art viewBox="0 0 260 160" className={className} label="Customers across the United States">
      {() => (
        <>
          <path
            d="M25.9 10.3L26.2 9.2L27.3 10L28.2 10.7L29.3 11.4L30.5 11.9L31.6 12L32.4 13L33 14L32.2 14.9L30.6 15.9L31.8 15.1L33.2 14.3L33.1 15.5L32.6 16.8L31.9 17.7L31.5 16.7L31.1 17.7L32.3 17.6L33.4 17L33.6 15.9L33.8 14.7L34.6 14L34.5 12.8L34.2 11.7L34.5 10.6L34.7 9.4L34 8.5L36.6 8.7L37.9 9.1L39.7 9.5L41.6 10L44 10.7L46.2 11.2L48.4 11.8L50.7 12.3L54.9 13.3L57.1 13.8L61.5 14.7L64.6 15.3L67.1 15.8L69.6 16.3L74.1 17.1L76.9 17.6L80 18L84.3 18.6L86.2 18.9L90.1 19.4L93.1 19.7L95 19.9L98.9 20.3L100.4 20.5L102.6 20.7L105.1 20.9L109.4 21.2L112.2 21.4L113.5 21.4L116 21.6L118.2 21.7L120 21.7L121.9 21.8L125.3 21.9L129.1 21.9L132.6 21.9L135.7 21.9L135.7 19.8L136.8 20.1L137.1 21.2L137.4 22.5L138 23.5L139.2 23.8L140.6 23.9L141.7 24.4L143 23.8L144.8 24.3L145.8 25L147.3 25.2L148.1 26L149.3 26.5L150.5 26.6L151.4 25.8L152.5 26.4L153.8 26.3L155.1 26.3L156.1 26.6L155.5 27.5L154 28.3L152.5 29L151.1 30.3L150.2 31.6L149.2 32.6L147.7 33.8L148.6 34.4L149.8 34L150.8 33.5L151.8 32.9L152.4 34.2L153.4 34.7L155 34.3L156.2 33.4L157.5 33.1L158.8 32.2L160 31.2L161.2 30.3L162.3 29.3L163.7 29.1L162.9 30.1L162.2 31.1L161.5 32L161.4 33.3L162.3 32.2L163.6 32.2L164.9 32.8L165.5 33.8L166.9 34.2L168 34.5L169.2 33.9L170.5 32.9L172.1 32.7L173.8 32.1L175 31.9L174.8 32.9L175.8 33.6L176.9 33.5L177.9 32.8L178.5 34L178.8 35.2L180 35.6L181.2 35.5L180.1 35.9L178.7 35.9L177.2 36L176.4 37L175.2 36.1L174 35.8L172.9 36.3L171.9 36.7L170.7 36.9L170.1 37.9L169.3 38.7L169.4 37.4L168.4 38.2L167.1 38.9L166.6 40.3L165.6 42.1L164.8 43.2L164.3 44.5L165.8 43.7L166.7 42.7L167 41.7L168 40.9L167.8 42.1L167.3 43.2L166.9 44.2L166.3 46.5L166 47.8L165.7 49.1L165.9 50.2L165.6 51.5L165.3 52.8L165.5 54L166.1 55.2L166.1 56.9L166.3 58.5L166.9 59.4L167.5 60.9L168.5 61.6L170.1 61L171.3 59.8L171.7 58.5L172.4 57.1L172.5 55.7L172.4 54.2L172 52.8L171.4 51.7L171 50.6L170.9 49.3L170.8 48.1L171.1 46.5L171.3 45.3L171.2 44.1L171.9 43.2L172.8 42.4L173.3 41.2L173.6 42.4L174.4 41.6L174.3 40.4L175.5 39.7L175.1 38.6L175.7 37.6L177.2 37.6L178.6 37.8L179.8 38.5L181.2 39.1L182.3 40L182.1 41.3L182.8 42.3L182.9 43.4L183 44.6L182.2 45.7L181.3 46.8L181 48L181.9 48.8L182.9 48.1L183 46.9L184.3 46.3L185.6 46.2L186.3 47.4L186.8 49.2L187.1 50.3L187.6 51.5L187.6 52.7L187.6 53.8L186.4 53.7L186.3 54.9L185.4 56.2L185.3 57.5L184.4 58.8L185.6 59.6L186.7 60.1L188.1 60.4L189.3 60.2L190.5 59.6L191.7 59.6L192.4 58.7L193.3 57.8L194.4 57.1L196 56.2L197.5 55.2L198.4 54.4L199.5 53.6L200.5 52.4L201.4 51.5L202.2 50.4L201.4 49.4L200.9 48L202.5 47.1L204.1 46.7L205.9 46.5L207 46.9L208.1 46.4L209.3 46.2L210.2 45.6L210.9 44.7L211.9 43.9L211.6 42.7L210.9 41.5L211.3 40.3L212.1 39.3L213 37.7L214 36.3L214.8 35.5L216 34.9L217.5 34.6L219.7 34L222.3 33.3L223.9 32.9L225.3 32.6L228.4 31.7L228.5 30.6L229.5 29.8L230.6 29.5L230.6 28.4L231 27.2L231.5 26L231.5 24.9L231.3 23.6L231.6 21.1L233.2 16.2L234.2 17.1L235.4 16.8L236.6 16.2L238 16.1L239.1 16.7L239.9 19.2L240.9 22.4L241.1 23.6L242.2 24.3L243 25.1L243.6 26.4L244.9 26.4L245.7 27.3L245.4 29L244.4 29.8L243.3 30.2L242.7 31.3L242 32.2L241.1 31.5L240.9 32.8L241 34L240.2 33.1L239.5 32.2L239 33.3L239.3 34.6L238.6 35.6L237.4 36.2L236.6 37.1L235.5 37.6L234.9 38.8L234.5 39.9L234.4 41.4L234.1 42.8L234.9 43.7L234.4 45L234.2 46.1L235.5 46.2L236 47.2L236.8 48.1L237.9 48.3L238.9 47.7L238.4 46.7L239.2 47.5L239.4 48.8L238.2 48.8L237.4 49.5L236.3 50.4L236.6 49L235.6 49.6L234.9 50.5L234.2 49.4L233.5 50.4L233.7 51.5L232.2 52.3L231.1 52.6L230.1 53L229 53.3L227.9 53.6L227.1 54.4L226 55.4L225 56.4L226.1 55.9L227.5 55.7L229 55.1L230.1 54.3L230.9 53.5L231.9 54.1L230.7 55.3L228.9 56.5L227.7 57.4L226.3 58L224.8 58.6L223.5 59.2L224.7 59.4L225 61L225 62.2L224.8 64.3L224.3 65.5L223.6 66.5L223.2 68L222.3 67L221 66.8L219.8 66.1L219.2 65.2L219.6 66.3L220.4 67.3L221 68.5L221.8 69.3L222.8 71L222.7 73.1L222.4 74.1L221.6 75.2L221.5 76.5L221.2 77.8L221 79L220 78.4L220.1 77.1L220.4 75.7L220.8 74.5L219.8 73.9L219.5 72.4L218.8 73.4L217.8 72.8L217.3 71.7L218.5 71.5L217.4 70.7L217.4 69.6L216.9 68.5L217.2 67.3L217.6 65.9L216.8 67.3L216.1 68.2L216.5 69.3L216.2 70.5L216.4 71.6L217.1 72.6L217.4 73.7L216.2 73.9L215 73.8L213.7 73.6L213.6 72.3L213.2 73.6L214.4 73.3L214.9 74.2L216.1 74.4L217.1 74.9L218.4 75.4L218.4 76.5L217.1 76.4L216 75.7L217 76.5L218 77.2L219 78.2L218.3 79.1L219.2 79.9L217.9 80.2L216.8 79.5L217.6 80.5L218.5 81.1L219.6 80.6L220.9 81.1L221.5 82.3L222.2 83.8L223.2 85.2L224 86.3L223.1 85.4L222.1 83.9L221.5 82.9L221.9 84.1L220.8 84.2L220.6 85.3L219.5 85L219.1 86.1L218.3 85.2L218.7 86.2L219.9 86.1L221.4 85.7L221.9 87.5L221.9 86.2L223.1 86.3L223.3 87.4L222.4 88.2L221.9 89.3L220.5 89.5L219.6 88.7L218.6 89.4L220.3 89.8L220.2 91.1L219.3 92.1L220.7 91.5L221.2 92.5L220.4 93.3L221.6 92.2L222.5 90.8L221.9 91.9L221.2 93.1L219.6 93.6L218.2 94.2L216.9 95.4L216.1 96.5L215.5 98L215.3 99.3L213.8 99.2L212.5 99.9L211.6 100.6L210.9 101.7L210.4 102.8L210.5 103.9L209.8 105.2L208.7 105.7L207.8 106.9L207.1 107.9L205.8 108.9L204.9 110.2L203.9 111.3L203.4 112.5L202.8 113.7L202.3 114.7L202.4 116L202.2 117.1L202.2 118.4L202.1 119.5L202.3 120.7L202.8 121.7L203.5 123.5L204.5 125.7L205.3 127L206 128L207.4 129.8L208.2 130.6L208.3 131.8L208.7 133.3L209.9 135.1L210.5 136.2L211.3 137.7L212 138.8L212.5 140L212.7 141.1L212.8 143L212.9 144.5L213 145.7L212.3 146.7L212.4 147.9L212.4 149.3L211.5 150.5L211.9 149.3L210.6 149.6L209.2 150L208.2 149.2L207.8 148L207.1 147L205.8 146.4L204.7 146L204.1 144.5L203.2 143.3L202.2 142.5L202.4 141.2L201.5 141.9L200.3 140.4L199.7 139.4L198.8 138.5L199.3 137.5L199.8 136.5L199.1 135.5L198.9 136.9L197.7 136.3L197.6 135.1L197.9 133.8L198.1 132.5L197.7 131.4L197.2 129.9L196 129.1L195 128.3L193.8 127.5L193.2 126.5L192.4 125.6L190.8 124.9L189.7 124.9L189 125.8L187.5 126.4L186.5 127.2L185.3 127.4L186.9 127.3L186 128L184.8 127.7L183.8 126.9L182.8 125.8L181.1 124.9L179.2 124.4L177.6 124.4L176.3 124.7L175.2 125L173.9 125.3L172.8 125.7L171.1 125.9L172.2 125.7L171.3 124.5L170.9 123.4L170.5 124.5L170.2 125.5L169 125.4L167.9 125.4L166.8 125.4L165.4 125.8L164.3 126.3L163.4 126.9L162.5 127.6L163.3 128.6L164 127.5L165.4 127.8L165 128.9L164 129.6L163.9 130.7L165.1 131.3L166.2 131.8L166.1 133.1L165.1 133.5L165.4 132.3L163.8 132L162.6 131.7L161.6 132.5L160.5 132.6L159.7 131.7L158.8 132.5L158 133.5L157 132.9L155.9 132.6L155.9 131.4L154.7 131.3L153.7 130.4L152.5 129.8L151.4 129.9L150.8 131.1L149 130.9L147.4 130.2L146.2 129.9L144.8 130L143.5 130.2L142.2 130.5L139.4 131.8L138.9 130.5L137.8 130.7L137.7 131.9L137.3 133.1L138.3 132.5L137.3 133.6L136.3 134.5L135.3 135.2L134.1 135.9L132.5 136.7L131.4 137.3L130.3 138.3L129 139.1L128.1 139.8L127.3 140.9L126.5 142.2L125.8 143.8L125.6 145.2L125.7 146.7L126.2 148.3L126.5 149.4L126.7 150.7L126.2 148.8L125.8 147.5L125.5 146.2L125.5 144.9L125.7 143.6L126.4 142L126.9 140.9L128 139.5L129.5 138.5L130.4 137.8L129.5 138.2L128.2 139L126.8 139.3L126.6 140.9L125.3 140.7L126 141.6L125.7 143.3L124.8 143.9L125.3 145.6L125.2 147.7L126 149.5L126.1 150.6L125.5 152L124.5 151.4L123.4 150.7L122.2 150.7L121.1 150.6L120 149.8L118.9 149.5L117.5 148.7L116.5 147.9L116.2 146.7L115.6 145.7L115.3 144.5L115.1 143L114.8 141.9L113.8 141.2L113.2 140.2L112.6 139.3L111.7 138.5L111.1 137.3L110.6 136.1L110.2 134.9L109.7 133.8L109.2 132.9L108.4 131.9L107.4 131.1L106.7 130.1L105.9 129.3L104.8 129.3L103.5 129L102.3 128.7L100.9 129L99.8 129.7L99.2 130.8L99 131.9L97.9 132.7L96.8 133.2L95.7 132.2L94.5 131.4L93.3 131L92.5 130.2L91.7 129.4L91 128.3L90.5 127.1L90.6 125.9L90.1 124.8L89.8 123.6L88.8 122.6L87.5 121.8L86.7 120.4L85.5 119.2L84.6 118.4L83.9 117.6L83.4 116.4L80.5 115.8L76.5 115.3L74.8 115.1L74.5 117.6L70.5 117.1L66 116.4L63.9 116.1L61 115.7L55.3 112.4L52 110.5L50.4 109.5L44.7 106.1L45.4 104.9L38.4 104.1L34.1 103.6L33.8 102.4L34 100.8L33.4 98.8L32.8 97.9L32.1 97L31.3 96L30.1 95.7L30 94.5L29.1 93.7L27.8 93.4L26.8 92.5L26.3 91.4L25.1 90.5L23.7 89.9L22.4 89.5L21.3 88.7L21.3 87.6L21.7 86.2L21.5 85.1L21.2 84L20.5 82.7L19.8 81.5L19.4 80.4L19 79L18.3 78L18.2 76.6L19.1 75.7L19.2 74.3L17.9 73.4L17.3 72.2L17.6 70.8L17.7 69.5L18.5 70.3L19.3 71.2L19.1 69.8L18.8 68.7L19.2 67.7L18.1 68.1L16.9 67.6L16.4 66.5L16.5 65.4L16 64.1L15.3 62.6L14.7 61.6L14.9 60.5L14.8 59.2L15.4 57.7L15.5 56.1L15 54.8L14.1 53.1L14.5 51.7L15.9 50.2L16.3 49.1L17.3 47.1L17.4 45.9L17.3 44.8L17.4 43.6L17.3 42.3L17.9 40.7L17.7 39L18.5 37.9L19.1 36.9L19.9 35.8L21 33.7L21.8 31.9L22.2 30.6L22.7 29.4L23.4 27.8L24 26.6L24.3 25.4L24.8 24.2L24.9 23L25.4 21.4L26.3 22L25.6 21L25.8 19.2L25.5 20.6L26.2 19.3L26 17.7L26.2 16.4L26 15.1L26.2 13.7L26.2 12.4L25.8 11.4Z"
            fill="var(--color-surface)"
            {...ink}
            strokeOpacity="0.45"
            strokeWidth="1.1"
          />
          <g fill="var(--color-bronze)" fillOpacity="0.55">
        <circle cx="224.3" cy="57.8" r="1.9" />
        <circle cx="31.3" cy="93.9" r="1.9" />
        <circle cx="167.3" cy="60.1" r="1.9" />
        <circle cx="135.4" cy="130" r="1.9" />
        <circle cx="58.1" cy="103.4" r="1.9" />
        <circle cx="220.3" cy="62.8" r="1.9" />
        <circle cx="120.3" cy="132" r="1.9" />
        <circle cx="34" cy="102.6" r="1.9" />
        <circle cx="128.8" cy="113.1" r="1.9" />
        <circle cx="124.3" cy="127.1" r="1.9" />
        <circle cx="201.3" cy="122" r="1.9" />
        <circle cx="187.6" cy="68.6" r="1.9" />
        <circle cx="201.2" cy="94.1" r="1.9" />
        <circle cx="174.2" cy="71.4" r="1.9" />
        <circle cx="33.8" cy="16" r="1.9" />
        <circle cx="94.2" cy="72.3" r="1.9" />
        <circle cx="233.7" cy="45.5" r="1.9" />
        <circle cx="173.7" cy="91.9" r="1.9" />
        <circle cx="185.9" cy="55.8" r="1.9" />
        <circle cx="29.1" cy="26.8" r="1.9" />
        <circle cx="47.6" cy="85.3" r="1.9" />
        <circle cx="212.6" cy="145.6" r="1.9" />
        <circle cx="186.1" cy="104.8" r="1.9" />
        <circle cx="143.2" cy="44.1" r="1.9" />
        <circle cx="138.6" cy="77.5" r="1.9" />
        <circle cx="157.7" cy="79.6" r="1.9" />
        <circle cx="66" cy="62.3" r="1.9" />
        <circle cx="214" cy="70.5" r="1.9" />
          </g>
          <circle cx="180.2" cy="45.8" r="3.4" fill="var(--color-bronze)" />
        </>
      )}
    </Art>
  );
}

////////// 7. DECORATION //////////

export function Ornament({ className }) {
  return (
    <svg viewBox="0 0 120 12" className={clsx("h-3 w-28", className)} fill="none" aria-hidden="true">
      <path d="M2 6h38M80 6h38" stroke="var(--color-rule)" strokeWidth="1" />
      <path d="M48 6l6-5 6 5-6 5Z" fill="var(--color-bronze-soft)" />
      <path d="M64 6l4-3.5 4 3.5-4 3.5Z" fill="var(--color-bronze-soft)" opacity="0.6" />
    </svg>
  );
}
